import { FC, useEffect, useState } from "react"
import { PluginWrapper, ToolbarWrapper } from "../../../components/plugins"
import useGridFilter, { CreateSelectorGridFilter } from "../../../system/hooks/useGridFilter";
import { useAppContext } from "../../../system/providers/appContextProvider"
import { DefaultGrid } from "../../../components/grids/default/defaultGrid";
import { LoadingStatus } from "../../../@types/enumsGlobal";
import { IDiscountProgramFormViewDTO } from "../../../libs/coreapi-dto/dirs/discountProgram";
import { DiscountProgramProvider } from "../../../Services/DataProviders/DiscountProgramProvider";
import DefaultDictionaryCommandsPanelV2 from "../../../components/commandsPanels/DefaultDictionaryCommandsPanelV2";
import { DateTime } from "luxon";
import { IDatePeriod } from "../../../components/datePeriodPicker/datePeriodPicker";
import { Spinner } from "../../../components/spiner/Spinner";

type IDiscountProgramFormViewDTOWithDisplay = {
    dateStarDisplay: string;
    dateEndDisplay: string;
    dateDeleted: string;
    dateModified: string;
    dateCreated: string;
    dateStart: string;
    dateEnd: string;
};

function formatDiscountProgramRow(item: IDiscountProgramFormViewDTO): IDiscountProgramFormViewDTOWithDisplay {
    const {
        dateCreated,
        dateStart,
        dateEnd,
        dateDeleted,
        dateModified,
        ...rest
    } = item;
    const toDateStr = (v: string | DateTime | null | undefined): string => {
        if (v == null) return '';
        if (DateTime.isDateTime(v)) return v.toFormat('dd.MM.yyyy HH:mm');
        return DateTime.fromISO(v).toFormat('dd.MM.yyyy HH:mm');
    };
    return {
        ...rest,
        dateCreated: dateCreated != null ? (DateTime.isDateTime(dateCreated) ? dateCreated.toFormat('dd.MM.yyyy HH:mm') : DateTime.fromISO(dateCreated).toFormat('dd.MM.yyyy HH:mm')) : '',
        dateStart: DateTime.fromISO(dateStart as string).toFormat('dd.MM.yyyy'),
        dateEnd: dateEnd != null ? (DateTime.isDateTime(dateEnd) ? dateEnd.toFormat('dd.MM.yyyy') : DateTime.fromISO(dateEnd).toFormat('dd.MM.yyyy')) : '',
        dateStarDisplay: DateTime.fromISO(dateStart as string).toFormat('dd.MM.yyyy'),
        dateEndDisplay: dateEnd != null ? (DateTime.isDateTime(dateEnd) ? dateEnd.toFormat('dd.MM.yyyy') : DateTime.fromISO(dateEnd).toFormat('dd.MM.yyyy')) : '',
        dateDeleted: toDateStr(dateDeleted as string | DateTime | null | undefined),
        dateModified: toDateStr(dateModified as string | DateTime | null | undefined),
    };
}

const DiscountProgramFormGrid: FC<IGridProps> = (props) => {    
    const appCtx = useAppContext();
    const discountProgramProvider = new DiscountProgramProvider(appCtx.coreApiService);

    const DefaultDatePeriod = { startDate: DateTime.now().plus({ days: -7 }), endDate: DateTime.now() } as IDatePeriod;
    const [selectedItem, setSelectedItem] = useState<IGridRow>();
    const defaultGridColumnFilter = {
        numberPerPage: 15,
        pageNumber: 1,
        fieldOrderBy: "dateCreated" as const,
        orderDesc: true,
        columnFilters:  [
            {
                name: "deleted",
                value: false,
                operator: "Eq"
            }
        ] as IGridColumnFilter[],
        paramFilter: {		
            dateStart: DefaultDatePeriod.startDate,		
            dateEnd: DefaultDatePeriod.endDate		
        }
    }
    const [gridFilter, dispatchGridFilter] = useGridFilter(defaultGridColumnFilter);
    const [viewState, setViewState] = useState<GridStateType>("view");
    const [data, setData] = useState<IDiscountProgramFormViewDTOWithDisplay[]>([]);
    const [loadingStatus, setLoadingStatus] = useState<LoadingStatus>(LoadingStatus.InProcess);
    const [totalCount, setTotalCount] = useState(0);
    const [multipleSelect, setMultipleSelect] = useState<boolean>(false);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(true);

    const getViewFormatted = (filter: IGridFilter, callback: (data: IDiscountProgramFormViewDTOWithDisplay[], totalCount?: number) => void) => {
        discountProgramProvider.getView(filter, (data, totalCount) => {
            callback(data.map(formatDiscountProgramRow), totalCount);
        });
    };

    useEffect(() => {
        setIsSubmitting(true)
        discountProgramProvider.getView(gridFilter, (data, totalCount) => {
            const formattedData = data.map(formatDiscountProgramRow);
            setData(formattedData)
            setTotalCount(totalCount)
            if (viewState == 'refresh') setViewState('view')
            if (totalCount === 0) {
                setLoadingStatus(LoadingStatus.NoData)
            } else {
                setLoadingStatus(LoadingStatus.Completed)
            }
            setIsSubmitting(false)
        })
    }, [gridFilter, viewState])

    return (
        <PluginWrapper>
            {
                <ToolbarWrapper>
                    <DefaultDictionaryCommandsPanelV2
                        selectedItem={[selectedItem, setSelectedItem]}
                        gridFilter={[gridFilter, dispatchGridFilter]}
                        plugin="discount_program_creator_plugin"
                        DefaultDatePeriod={DefaultDatePeriod}
                        multipleSelect={[multipleSelect, setMultipleSelect]}
                        dataProvider={discountProgramProvider}
                        pluginSettings={props.plugin}
                        setViewState={(vs) => {
                            setViewState(vs)
                        }}
                        isInactiveToPrint
                    />
                </ToolbarWrapper>
            }
            {
                isSubmitting ? <Spinner /> : <DefaultGrid
                    showQuantity
                    hiddenPagination={undefined}
                    gridId={props.gridId}
                    data={data}
                    separator
                    loadingStatus={loadingStatus}
                    setLoadingStatus={setLoadingStatus}
                    totalCount={totalCount}
                    filter={gridFilter}
                    plugin={props.plugin}
                    getView={getViewFormatted}
                    dataProvider={discountProgramProvider}
                    selectedItem={selectedItem}
                    onSelect={(row) => {
                        setSelectedItem(row)
                        props.onSelect?.(row)
                    }}
                    onDoubleClick={(i) => props.onDoubleClick?.(i)}
                    onSort={(i) => dispatchGridFilter({ type: "sort", payload: i.propertyName })}
                    onFilterDelete={(i) => dispatchGridFilter({ type: "deleteColumnFilter", payload: i.propertyName })}
                    onPageNumberChange={(n) => dispatchGridFilter({ type: "changePageNumber", payload: { pageNumber: n } })}
                    onNumberPerPageChange={(n) => dispatchGridFilter({ type: "changeNumberPerPage", payload: { numberPerPage: n } })}
                    onEnter={() => setViewState("edit")}
                />
            }
        </PluginWrapper>
    )
}

export default DiscountProgramFormGrid